import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Calendar, Sparkles, CheckCircle, Trash2, History, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emotions = [
  { id: 'Alto Bom Humor', label: 'Alto Bom Humor', image: '/images/alto-bom-humor.png', color: 'bg-green-100 border-green-300 hover:bg-green-200' },
  { id: 'Bom Humor', label: 'Bom Humor', image: '/images/bom-humor.png', color: 'bg-lime-100 border-lime-300 hover:bg-lime-200' },
  { id: 'Neutro', label: 'Neutro', image: '/images/neutro.png', color: 'bg-gray-100 border-gray-300 hover:bg-gray-200' },
  { id: 'Mau Humor', label: 'Mau Humor', image: '/images/mau-humor.png', color: 'bg-orange-100 border-orange-300 hover:bg-orange-200' },
  { id: 'Alto Mau Humor', label: 'Alto Mau Humor', image: '/images/alto-mau-humor.png', color: 'bg-red-100 border-red-300 hover:bg-red-200' }
];

const needs = [
  'Compreensão',
  'Conexão',
  'Autonomia',
  'Segurança',
  'Reconhecimento',
  'Crescimento',
  'Contribuição',
  'Paz',
  'Criatividade',
  'Diversão'
];

const EmotionDiary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [formData, setFormData] = useState({
    what_happened: '',
    thoughts: '',
    body_sensations: '',
    needs: [],
    next_step: ''
  });
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchEntries();
    fetchInsights();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/emotion-diary/entries?limit=5`, {
        withCredentials: true
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const fetchInsights = async (manual = false) => {
    if (manual) setIsGeneratingInsights(true);
    else setIsLoading(true);
    setInsightsError(null);
    try {
      const response = await axios.get(`${API}/emotion-diary/insights`, {
        withCredentials: true
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      if (manual) setInsightsError('Não foi possível gerar insights agora. Tente novamente em instantes.');
    } finally {
      if (manual) setIsGeneratingInsights(false);
      else setIsLoading(false);
    }
  };

  const handleNeedToggle = (need) => {
    if (formData.needs.includes(need)) {
      setFormData({
        ...formData,
        needs: formData.needs.filter(n => n !== need)
      });
    } else {
      setFormData({
        ...formData,
        needs: [...formData.needs, need]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedEmotion) {
      alert('Por favor, selecione uma emoção');
      return;
    }

    setIsSaving(true);
    
    try {
      await axios.post(
        `${API}/emotion-diary/entries`,
        {
          emotion: selectedEmotion,
          ...formData
        },
        { withCredentials: true }
      );
      
      // Reset form
      setSelectedEmotion(null);
      setFormData({
        what_happened: '',
        thoughts: '',
        body_sensations: '',
        needs: [],
        next_step: ''
      });
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      // Refresh data
      fetchEntries();
      fetchInsights();
      
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Erro ao salvar registro');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Tem certeza que deseja deletar este registro?')) return;
    
    try {
      await axios.delete(`${API}/emotion-diary/entries/${entryId}`, {
        withCredentials: true
      });
      fetchEntries();
      fetchInsights();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Erro ao deletar registro');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button onClick={() => navigate('/diary/history')} variant="outline">
              <History className="w-4 h-4 mr-2" />
              Ver Histórico
            </Button>
          </div>
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Diário de Gatilhos</h1>
            <p className="text-gray-600">Registre e reflita sobre suas emoções para desenvolver maior autoconsciência e inteligência emocional</p>
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <Card className="mb-6 p-4 bg-green-50 border-green-200">
            <div className="flex items-center space-x-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Registro salvo com sucesso!</span>
            </div>
          </Card>
        )}

        {/* New Entry Form */}
        <Card className="p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Novo Registro</h2>
          
          {/* Emotion Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Como você está se sentindo hoje?
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {emotions.map((emotion) => (
                <button
                  key={emotion.id}
                  onClick={() => setSelectedEmotion(emotion.id)}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    selectedEmotion === emotion.id
                      ? 'ring-2 ring-purple-500 scale-105'
                      : ''
                  } ${emotion.color}`}
                >
                  <img 
                    src={emotion.image} 
                    alt={emotion.label} 
                    className="w-full max-w-[80px] h-auto mx-auto mb-2 object-contain aspect-square"
                  />
                  <div className="text-xs sm:text-sm font-medium text-gray-700 leading-tight">{emotion.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Reflection Questions - Show only if emotion selected */}
          {selectedEmotion && (
            <form onSubmit={handleSubmit} className="space-y-6 animate-fadeIn">
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-purple-700 mb-4">
                  Vamos refletir sobre sua experiência
                </h3>

                {/* What happened */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    O que aconteceu?
                  </label>
                  <textarea
                    value={formData.what_happened}
                    onChange={(e) => setFormData({ ...formData, what_happened: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    placeholder="Descreva o que aconteceu..."
                    required
                  />
                </div>

                {/* Thoughts */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quais pensamentos surgiram?
                  </label>
                  <textarea
                    value={formData.thoughts}
                    onChange={(e) => setFormData({ ...formData, thoughts: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    placeholder="O que passou pela sua cabeça?"
                    required
                  />
                </div>

                {/* Body sensations */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    O que você sentiu no corpo?
                  </label>
                  <textarea
                    value={formData.body_sensations}
                    onChange={(e) => setFormData({ ...formData, body_sensations: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={2}
                    placeholder="Ex: aperto no peito, tensão nos ombros, borboletas no estômago..."
                    required
                  />
                </div>

                {/* Needs */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Que necessidades estavam em jogo?
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {needs.map((need) => (
                      <button
                        key={need}
                        type="button"
                        onClick={() => handleNeedToggle(need)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          formData.needs.includes(need)
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-purple-300'
                        }`}
                      >
                        {need}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Next step */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qual seu próximo passo?
                  </label>
                  <textarea
                    value={formData.next_step}
                    onChange={(e) => setFormData({ ...formData, next_step: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={2}
                    placeholder="O que você pode fazer para cuidar dessas necessidades?"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSaving}
                  className="w-full bg-purple-600 hover:bg-purple-700 py-3 text-lg"
                >
                  {isSaving ? 'Salvando...' : 'Salvar Registro'}
                </Button>
              </div>
            </form>
          )}
        </Card>

        {/* Insights Section */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">Insights da Simby</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchInsights(true)}
              disabled={isGeneratingInsights}
              className="border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              {isGeneratingInsights ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2" />
                  Gerando…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Gerar novo insight
                </>
              )}
            </Button>
          </div>

          {insightsError && (
            <p className="text-sm text-red-600 mb-3">{insightsError}</p>
          )}

          {insights && insights.has_insights ? (
            <>
              <div className="space-y-4">
                {insights.insights.map((insight, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">{insight.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{insight.title}</h3>
                        <p className="text-gray-700 text-sm">{insight.insight}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-gray-500">
                Baseado em {insights.total_entries} registro{insights.total_entries !== 1 ? 's' : ''} recente{insights.total_entries !== 1 ? 's' : ''}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              {isLoading ? 'Carregando insights…' : 'Faça um registro e clique em "Gerar novo insight" para receber uma análise personalizada da Simby.'}
            </p>
          )}
        </Card>

        {/* Recent Entries */}
        {entries.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">Registros Recentes</h2>
              </div>
            </div>
            
            <div className="space-y-4">
              {entries.map((entry) => {
                const emotion = emotions.find(e => e.id === entry.emotion);
                return (
                  <div key={entry.entry_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {emotion?.image && (
                          <img 
                            src={emotion.image} 
                            alt={emotion.label} 
                            className="w-12 h-12 object-contain"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-gray-900">{entry.emotion}</div>
                          <div className="text-xs text-gray-500">{formatDate(entry.created_at)}</div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDelete(entry.entry_id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>O que aconteceu:</strong> {entry.what_happened}
                    </div>
                    
                    {entry.needs && entry.needs.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {entry.needs.map((need, idx) => (
                          <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                            {need}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmotionDiary;

